import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import './ServiceDetailPage.css';
import { PageHero } from '../PageHero/PageHero';
import { ServiceFAQ, FAQItem } from './ServiceFAQ';
import { ServiceCTA } from './ServiceCTA';

interface Step {
  n: string;
  title: string;
  body: string;
}

export interface ServiceDetailPageProps {
  meta: { title: string; description: string; canonical: string };
  hero: { image: string; tag: string; heading: string; subheading: string };
  detail: { heading: string; subheading: string; features: string[]; image: string };
  steps: Step[];
  faq: FAQItem[];
  cta: { heading: string; subheading: string; primaryText: string; primaryTo: string; phone: string };
}

export const ServiceDetailPage = ({ meta, hero, detail, steps, faq, cta }: ServiceDetailPageProps) => {
  const detailRef = useRef<HTMLElement>(null);
  const stepsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const refs = [detailRef, stepsRef];
    const observers = refs.map(ref => {
      const obs = new IntersectionObserver(
        entries => entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
        }),
        { threshold: 0.07 }
      );
      ref.current?.querySelectorAll<HTMLElement>('.rn-reveal').forEach(el => obs.observe(el));
      return obs;
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const serviceName = meta.title.split(' — ')[0];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    provider: {
      '@type': 'Organization',
      name: 'Renuit Logistics',
      url: 'https://www.renuit.pro/',
    },
    description: meta.description,
    url: meta.canonical,
    areaServed: {
      '@type': 'Place',
      name: 'Southeast US',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.renuit.pro/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://www.renuit.pro/services' },
      { '@type': 'ListItem', position: 3, name: serviceName, item: meta.canonical },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.renuit.pro/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RenuitLogistics" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.renuit.pro/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <PageHero
        image={hero.image}
        tag={hero.tag}
        heading={hero.heading}
        subheading={hero.subheading}
      />

      {/* Detail: features + image */}
      <section className="rn-sd-detail" ref={detailRef}>
        <div className="rn-sd-detail__inner">
          <div className="rn-sd-detail__text rn-reveal">
            <span className="cs-topper">What's Included</span>
            <h2 className="cs-title">{detail.heading}</h2>
            <p className="rn-sd-detail__sub">{detail.subheading}</p>
            <ul className="rn-sd-detail__features">
              {detail.features.map(f => (
                <li key={f} className="rn-sd-detail__feature">{f}</li>
              ))}
            </ul>
          </div>
          <div className="rn-sd-detail__img-wrap rn-reveal rn-reveal-delay-2">
            <img
              src={detail.image}
              alt={hero.heading}
              className="rn-sd-detail__img"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="rn-sd-steps" ref={stepsRef}>
        <div className="rn-sd-steps__inner">
          <div className="rn-sd-steps__header rn-reveal">
            <span className="cs-topper">The Process</span>
            <h2 className="cs-title">How It Works</h2>
          </div>
          <ol className="rn-sd-steps__list">
            {steps.map(({ n, title, body }, i) => (
              <li key={n} className={`rn-sd-steps__item rn-reveal rn-reveal-delay-${(i % 3) + 1}`}>
                <span className="rn-sd-steps__num">{n}</span>
                <div className="rn-sd-steps__content">
                  <h3 className="rn-sd-steps__title">{title}</h3>
                  <p className="rn-sd-steps__body">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <ServiceFAQ items={faq} />

      {/* CTA */}
      <ServiceCTA
        heading={cta.heading}
        subheading={cta.subheading}
        primaryText={cta.primaryText}
        primaryTo={cta.primaryTo}
        phone={cta.phone}
      />
    </>
  );
};
